import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { campaignService } from '../services/campaignService';

export const CampaignFeed = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { currentUser } = useAuth();
  const [isMobile, setIsMobile] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [usePersonalizedFeed, setUsePersonalizedFeed] = useState(
    localStorage.getItem('usePersonalizedFeed') === 'true'
  );

  const categories = [
    { id: 'all', name: 'All Categories' },
    { id: 'environment', name: 'Environment' },
    { id: 'education', name: 'Education' },
    { id: 'health', name: 'Health' },
    { id: 'community', name: 'Community' },
    { id: 'technology', name: 'Technology' },
    { id: 'arts', name: 'Arts & Culture' },
    { id: 'business', name: 'Business' },
    { id: 'social', name: 'Social Impact' },
    { id: 'food', name: 'Food' },
    { id: 'creatives', name: 'Creatives' }
  ];

  const campaignsPerPage = 6;
  const totalPages = Math.ceil(filteredCampaigns.length / campaignsPerPage);
  const startIndex = currentPage * campaignsPerPage;
  const endIndex = startIndex + campaignsPerPage;
  const currentCampaigns = filteredCampaigns.slice(startIndex, endIndex);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const handleMatch = event => setIsMobile(event.matches);
    handleMatch(media);
    media.addEventListener('change', handleMatch);
    return () => media.removeEventListener('change', handleMatch);
  }, []);

  const mobileSlides = useMemo(() => {
    if (!isMobile) return [];
    const slides = [];
    const source = filteredCampaigns;
    for (let i = 0; i < source.length; i += 3) {
      slides.push(source.slice(i, i + 3));
    }
    return slides;
  }, [isMobile, filteredCampaigns]);

  const fetchCampaigns = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null); // Clear previous errors

      let response;
      if (currentUser && usePersonalizedFeed) {
        // Use personalized endpoint
        const token = localStorage.getItem('sbwom_token') || sessionStorage.getItem('sbwom_token');
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const res = await fetch(`${API_BASE_URL}/campaigns/personalized`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        response = await res.json();
      } else {
        // Use regular endpoint
        response = currentUser
          ? await campaignService.getCampaigns()
          : await campaignService.getPublicCampaigns();
      }

      if (Array.isArray(response)) {
        setCampaigns(response);
        setFilteredCampaigns(response);
      } else {
        // Don't show error if we have cached campaigns
        if (campaigns.length === 0) {
          setError('Invalid response format');
        }
      }
    } catch (err) {
      // Only show error if we don't have cached campaigns to display
      // This allows the app to work offline with cached data
      if (campaigns.length === 0) {
        setError('An error occurred while fetching campaigns');
      }
      console.error('Campaign fetch error (cached data retained):', err);
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch campaigns on mount and when currentUser or usePersonalizedFeed changes
  useEffect(() => {
    fetchCampaigns();
  }, [currentUser, usePersonalizedFeed]);

  // Refetch campaigns when app comes back into view (for PWA)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Silently refetch data when app becomes visible
        fetchCampaigns(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [currentUser]);

  useEffect(() => {
    let filtered = campaigns;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(campaign => campaign.category === selectedCategory);
    }

    if (searchTerm) {
      const lowered = searchTerm.toLowerCase();
      filtered = filtered.filter(
        campaign =>
          campaign.title.toLowerCase().includes(lowered) ||
          campaign.description.toLowerCase().includes(lowered)
      );
    }

    setFilteredCampaigns(filtered);
    setCurrentPage(0);
  }, [campaigns, searchTerm, selectedCategory]);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner" />
        <p>Loading campaigns...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="dashboard-shell dashboard-shell--compact dashboard-shell--tight">
      <div className="dashboard-header dashboard-header--centered">
        <div className="dashboard-header__content">
          <h1 className="dashboard-title">Campaign Library</h1>
          <p className="dashboard-subtitle">
            Explore opportunities and join the campaigns that inspire you.
          </p>
        </div>
        <button
          onClick={() => fetchCampaigns(true)}
          disabled={refreshing}
          className="dashboard-refresh-button"
          title="Refresh campaigns"
        >
          <svg
            className={`dashboard-refresh-icon ${refreshing ? 'dashboard-refresh-icon--spinning' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
              clipRule="evenodd"
            />
          </svg>
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div className="feed-controls feed-controls--tight">
        <div className="feed-control feed-control--search">
          <label htmlFor="search" className="feed-control__label">
            Search campaigns
          </label>
          <input
            type="text"
            id="search"
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
            placeholder="Search by title or description"
            className="feed-control__input"
          />
        </div>
        <div className="feed-control feed-control--select">
          <label htmlFor="category" className="feed-control__label">
            Category
          </label>
          <select
            id="category"
            value={selectedCategory}
            onChange={event => setSelectedCategory(event.target.value)}
            className="feed-control__input"
          >
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
        {currentUser && (
          <div className="feed-control feed-control--checkbox" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="personalized"
              checked={usePersonalizedFeed}
              onChange={(e) => {
                const newValue = e.target.checked;
                setUsePersonalizedFeed(newValue);
                localStorage.setItem('usePersonalizedFeed', newValue);
              }}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <label htmlFor="personalized" style={{ cursor: 'pointer', fontSize: '14px', color: '#5d4037' }}>
              Show personalized feed based on my interests
            </label>
          </div>
        )}
        <div className="feed-stats">
          <span>
            <strong>{campaigns.length}</strong> total
          </span>
          <span>
          </span>
        </div>
      </div>

      {filteredCampaigns.length === 0 ? (
        <div className="dashboard-empty">
          <h3>No campaigns match your filters</h3>
          <p>Try adjusting the search term or selecting a different category.</p>
        </div>
      ) : (
        <>
          {isMobile ? (
            <>
              <div className="dashboard-slider" aria-label="Campaign library">
                {mobileSlides.map((slide, index) => (
                  <div key={`slide-${index}`} className="dashboard-slide">
                    {slide.map(campaign => (
                      <div key={campaign.id} className="campaign-card">
                        <div className="campaign-card__header">
                          <div className="campaign-card__badge">
                            <svg className="campaign-card__badge-icon" viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-9-3a2 2 0 114 0 2 2 0 01-4 0zm-2 5a5 5 0 019.584 1.249A7.002 7.002 0 0110 17a7.002 7.002 0 01-6.584-3.751A5 5 0 017 12z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <div>
                            <h3 className="campaign-card__title">{campaign.title}</h3>
                            <p className="campaign-card__subtitle">Campaign</p>
                          </div>
                        </div>

                        <div className="campaign-card__meta">
                          <div className="campaign-card__chip">
                            <svg viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span>{formatDate(campaign.createdAt)}</span>
                          </div>
                          <div className="campaign-card__chip">
                            <svg viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span>Campaign Owner</span>
                          </div>
                        </div>
                        <Link to={`/campaigns/${campaign.id}`} className="campaign-card__button">
                          View Details
                        </Link>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="dashboard-slider__hint" aria-hidden="true">
                Swipe to browse →
              </div>
            </>
          ) : (
            <div className="dashboard-grid">
              {currentCampaigns.map(campaign => (
                <div key={campaign.id} className="campaign-card">
                  <div className="campaign-card__header">
                    <div className="campaign-card__badge">
                      <svg className="campaign-card__badge-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-9-3a2 2 0 114 0 2 2 0 01-4 0zm-2 5a5 5 0 019.584 1.249A7.002 7.002 0 0110 17a7.002 7.002 0 01-6.584-3.751A5 5 0 017 12z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="campaign-card__title">{campaign.title}</h3>
                      <p className="campaign-card__subtitle">Campaign</p>
                    </div>
                  </div>

                  <div className="campaign-card__meta">
                    <div className="campaign-card__chip">
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>{formatDate(campaign.createdAt)}</span>
                    </div>
                    <div className="campaign-card__chip">
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Campaign Owner</span>
                    </div>
                  </div>
                  <Link to={`/campaigns/${campaign.id}`} className="campaign-card__button">
                    View Details
                  </Link>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && !isMobile && (
            <div className="dashboard-pagination">
              <button
                onClick={goToPrevPage}
                disabled={currentPage === 0}
                className="dashboard-pagination__button"
              >
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              <span className="dashboard-pagination__status">
                {currentPage + 1} / {totalPages}
              </span>
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages - 1}
                className="dashboard-pagination__button"
              >
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CampaignFeed;
